// KY Report — 색상·라벨 토큰 (단일 소스)
// public/report.html 의 검증된 색상 언어를 React 모듈로 옮긴 단일 출처.
// 숙달 밴드/난이도 팔레트는 현재 LearningStatus/utils/constants.js ·
// FormRatingChartSection.jsx · ReportModal.js 에 로컬 중복되어 있다.
// 신규 리포트 UI 는 이 파일만 import 한다(기존은 점진 수렴).
//
// 토큰 정의 파일이므로 색상 하드코딩 규칙을 비활성화한다(src/styles/tokens.js 와 동일 컨벤션).
/* eslint-disable no-restricted-syntax */
import { HEX_COLORS } from '../../../styles/tokens';

// 성취 밴드: 정답률 기준 4단계 (text = 700계열 / fill = 파스텔 300계열 0.7 투명도 — 프로젝트 fill 관례와 동일)
export const BAND = {
  mastered: { text: '#15803D', fill: 'rgba(134, 239, 172, 0.7)', label: '우수' },
  mid: { text: '#A16207', fill: 'rgba(253, 224, 71, 0.7)', label: '양호' },
  weak: { text: '#B91C1C', fill: 'rgba(252, 165, 165, 0.7)', label: '보강' },
  unlearned: { text: '#9CA3AF', fill: '#E5E7EB', label: '미학습' }, // 중립 회색(트랙)
};

// 정답률 밴드 경계 [양호, 숙달] — 단일 출처(추후 데이터·설정 주입 지점)
export const BAND_LEVELS = [0.7, 0.85];

// 정답률 → 밴드 키
export function bandOf(accuracy) {
  if (accuracy == null) return 'unlearned';
  if (accuracy >= BAND_LEVELS[1]) return 'mastered';
  if (accuracy >= BAND_LEVELS[0]) return 'mid';
  return 'weak';
}

// 난이도 5단계 (index 0 = 없음/미상). 프로젝트 정본(LearningStatus/utils/constants.js·ProblemPreview·ReportModal)과 동일.
// SOLID = 텍스트·도트(진한색) / PASTEL = 배경 fill(파스텔, 투명도 0.7) — 프로젝트 관례 그대로.
export const DIFFICULTY_SOLID = ['#9CA3AF', '#EAB308', '#059669', '#2563EB', '#EA580C', '#DC2626'];
export const DIFFICULTY_PASTEL = [
  'rgba(156, 163, 175, 0.7)', // 0 없음/미상 (gray-400)
  'rgba(253, 224, 71, 0.7)',  // 1 노랑 (yellow-300)
  'rgba(134, 239, 172, 0.7)', // 2 초록 (green-300)
  'rgba(147, 197, 253, 0.7)', // 3 파랑 (blue-300)
  'rgba(253, 186, 116, 0.7)', // 4 주황 (orange-300)
  'rgba(252, 165, 165, 0.7)', // 5 빨강 (red-300)
];

export function difficultyColor(level, { pastel = false } = {}) {
  const palette = pastel ? DIFFICULTY_PASTEL : DIFFICULTY_SOLID;
  return palette[level] || palette[0];
}

// 리포트와 학생 목록의 레이팅 점수·차트가 같은 구간 색을 쓴다.
export function ratingColor(rating) {
  const level = !rating || rating <= 0 ? 0
    : rating <= 2000 ? 1 : rating <= 4000 ? 2 : rating <= 6000 ? 3 : rating <= 8000 ? 4 : 5;
  return difficultyColor(level);
}

// 수학 영역 라벨
export const MATH_TYPE_LABEL = { AL: '대수', GE: '기하', AN: '해석', PS: '확률·통계' };

// 개념 타입 라벨 — 앱 정본 명칭(AITrackerContent.getConceptTypeName·KMatrixContent.CONCEPT_TYPES와 동일).
// 주의: 정본은 IDN='정리'·THM='이론'(직역과 다름) — 화면 전체 용어 통일이 우선이므로 정본을 따른다.
export const CONCEPT_TYPE_LABEL = {
  DEF: '정의', AXM: '공리', IDN: '정리', THM: '이론',
  MTH: '방법론', PRP: '성질', FOR: '공식', NTN: '표기법',
};

// 수학 영역/개념 타입 배지 배경색 (프로젝트 공통 — AITracker·MathceptDetailModal와 동일 rgba)
// '_' = 미매핑 폴백. 배지 디자인 단일 출처.
export const MATH_TYPE_COLOR = {
  AL: 'rgba(99, 102, 241, 0.5)',
  AN: 'rgba(244, 63, 94, 0.5)',
  GE: 'rgba(16, 185, 129, 0.5)',
  PS: 'rgba(168, 85, 247, 0.5)',
  _: 'rgba(107, 114, 128, 0.5)',
};
export const CONCEPT_TYPE_COLOR = {
  DEF: 'rgba(239, 68, 68, 0.5)',
  AXM: 'rgba(249, 115, 22, 0.5)',
  IDN: 'rgba(245, 158, 11, 0.5)',
  THM: 'rgba(34, 197, 94, 0.5)',
  MTH: 'rgba(6, 182, 212, 0.5)',
  PRP: 'rgba(59, 130, 246, 0.5)',
  FOR: 'rgba(139, 92, 246, 0.5)',
  NTN: 'rgba(236, 72, 153, 0.5)',
  _: 'rgba(156, 163, 175, 0.5)',
};

// 차트 1차색(라인/포인트)·강조
export const ACCENT = HEX_COLORS.primary; // #4ABC85 (선·텍스트용 solid)
export const ACCENT_SOFT = 'rgba(74, 188, 133, 0.7)'; // ACCENT 0.7 — 게이지 호·진도바 fill (fill 관례와 동일 투명도)
export const RISK = HEX_COLORS.danger; // #DC2626

// 프리미티브 공용 색 (컴포넌트는 raw hex 대신 이 상수들을 import)
export const INK = HEX_COLORS.headerBg; // #231F20 게이지 텍스트
export const TRACK = HEX_COLORS.gray200; // #E5E7EB 게이지/바 트랙
export const INK_FADED = 'rgba(35,31,32,0.4)'; // 중립 텍스트(추세 칩 등)

// 스파크라인 선 색 (마지막 값 밴드 기준) — 밴드 텍스트(700계열)로 원색 회피
export const SPARK = {
  high: '#15803D', // green-700 = BAND.mastered.text
  mid: '#A16207', // amber-700 = BAND.mid.text
  low: '#B91C1C', // red-700 = BAND.weak.text
};
